/**
 * ErrorState - Displays error messages for failed API calls
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';

interface Props {
  error: Error | null;
  title?: string;
  message?: string;
  onRetry?: () => void;
  fullHeight?: boolean;
}

/**
 * Full-page error state - use sparingly, prefer InlineError for most cases
 */
function ErrorState({
  error,
  title = 'Failed to load data',
  message,
  onRetry,
  fullHeight = false,
}: Props) {
  const displayMessage = message || error?.message || 'An unexpected error occurred. Please try again.';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: fullHeight ? '100vh' : '400px',
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: 500, width: '100%', textAlign: 'center' }}>
        <ErrorOutline sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{title}</AlertTitle>
          {displayMessage}
        </Alert>

        {onRetry && (
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={onRetry}
            color="primary"
          >
            Retry
          </Button>
        )}

        {process.env.NODE_ENV === 'development' && error?.stack && (
          <Typography
            variant="caption"
            component="pre"
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
              textAlign: 'left',
              overflow: 'auto',
              fontSize: '0.7rem',
            }}
          >
            {error.stack}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface InlineErrorProps {
  error: Error | null;
  title?: string;
  onRetry?: () => void;
}

/**
 * Inline error alert - shows error within the page layout without replacing the page
 */
export function InlineError({ error, title = 'Failed to load data', onRetry }: InlineErrorProps) {
  return (
    <Alert 
      severity="error" 
      sx={{ mb: 3 }}
      action={
        onRetry && (
          <Button color="inherit" size="small" onClick={onRetry} startIcon={<Refresh />}>
            Retry
          </Button>
        )
      }
    >
      <AlertTitle>{title}</AlertTitle>
      {error?.message || 'Network Error'}
    </Alert>
  );
}

export default ErrorState;
